export type Direction = 'ai' | 'user';

export const MESSAGE_TYPES = {
  MESSAGE: 'message',
  OPTIMISTIC: 'optimistic',
  ERROR: 'error',
  SYSTEM: 'system',
  PROPOSAL: 'proposal',
  SEARCH_SUPPORT_INSIGHT: 'search_support_insight',
  SUMMARY: 'summary',
  RESERVATION_INFO_FORM: 'reservation_info_form',
  RESERVATION_INFO_CONFIRMED: 'reservation_info_confirmed',
  ARRANGEMENT_CONFIRMATION: 'arrangement_confirmation',
  ARRANGEMENT_COMPLETED: 'arrangement_completed'
} as const;

export type MessageType = typeof MESSAGE_TYPES[keyof typeof MESSAGE_TYPES];

export type BasePusherPayload = {
  id: string;
  trip_id: number;
  direction: Direction;
  text: string;
  created_at: string;
};

export type SummaryItem = {
  category: 'outward' | 'homeward' | 'hotel';
  label: string;
  text: string;
  price: number | null;
  priceWithTax: number | null;
};

export type RawSummaryItem = {
  category: 'outward' | 'homeward' | 'hotel';
  label: string;
  text: string;
  price: number | null;
  price_with_tax: number | null;
};

export type Summary = {
  items: SummaryItem[];
  totalPrice: number;
  totalPriceWithTax: number;
  hasSelection: boolean;
};

export type RawSummary = {
  items: RawSummaryItem[];
  total_price: number;
  total_price_with_tax: number;
  has_selection: boolean;
};

export type InsightMetric = {
  label: string;
  value: string;
};

export type InsightColumn = {
  key: string;
  label: string;
};

export type InsightTable = {
  columns: InsightColumn[];
  rows: Record<string, string>[];
  highlightedDates?: string[];
};

export type RawInsightTable = {
  columns: InsightColumn[];
  rows: Record<string, string>[];
  highlighted_dates?: string[];
};

export type RawSearchSupportInsight = {
  title: string;
  summary: string;
  metrics: InsightMetric[];
  table?: RawInsightTable;
  next_action?: string;
  coverage?: string;
  components?: Record<string, unknown>;
  missing_requirements?: string[];
};

export type SearchSupportInsight = {
  title: string;
  summary: string;
  metrics: InsightMetric[];
  table?: InsightTable;
  nextAction?: string;
  coverage?: string;
  components?: Record<string, unknown>;
  missingRequirements?: string[];
};

export type ProposalBundleItem = {
  id: number;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  price: number | null;
  priceText: string | null;
  aiHighlightTags: string[] | null;
  metadata: Record<string, unknown> | null;
};

export type ProposalBundle = {
  id: number;
  category: string;
  metadata: Record<string, unknown> | null;
  items: ProposalBundleItem[];
};

export type ChatMessage = {
  id: string;
  direction: Direction;
  text: string;
  createdAt: string;
  type?: MessageType;
  proposalBundles?: ProposalBundle[];
  proposalBundleIds?: number[];
  proposalItemIds?: number[];
  summary?: Summary;
  insight?: SearchSupportInsight;
  editable?: boolean;
  orderId?: number;
  approvalRequired?: boolean;
  isNewlyCompleted?: boolean;
  inputDisabled?: boolean;
  proposalChangeDisabled?: boolean;
  isNew?: boolean;
};

export type PayloadByType = {
  [MESSAGE_TYPES.PROPOSAL]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.PROPOSAL;
    direction: 'ai';
    ai_message_id: number;
    bundle_ids: number[];
    item_ids: number[];
  };
  [MESSAGE_TYPES.SEARCH_SUPPORT_INSIGHT]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.SEARCH_SUPPORT_INSIGHT;
    direction: 'ai';
    insight: RawSearchSupportInsight;
  };
  [MESSAGE_TYPES.SUMMARY]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.SUMMARY;
    direction: 'ai';
    summary: RawSummary;
  };
  [MESSAGE_TYPES.RESERVATION_INFO_FORM]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.RESERVATION_INFO_FORM;
    editable?: boolean;
  };
  [MESSAGE_TYPES.MESSAGE]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.MESSAGE;
  };
  [MESSAGE_TYPES.ERROR]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.ERROR;
  };
  [MESSAGE_TYPES.SYSTEM]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.SYSTEM;
  };
  [MESSAGE_TYPES.OPTIMISTIC]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.OPTIMISTIC;
  };
  [MESSAGE_TYPES.ARRANGEMENT_CONFIRMATION]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.ARRANGEMENT_CONFIRMATION;
    direction: 'ai';
    summary: RawSummary;
  };
  [MESSAGE_TYPES.ARRANGEMENT_COMPLETED]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.ARRANGEMENT_COMPLETED;
    direction: 'ai';
    order_id: number;
    approval_required: boolean;
  };
  [MESSAGE_TYPES.RESERVATION_INFO_CONFIRMED]: BasePusherPayload & {
    type: typeof MESSAGE_TYPES.RESERVATION_INFO_CONFIRMED;
    direction: 'ai';
    input_disabled: boolean;
    proposal_change_disabled: boolean;
  };
};

export type PusherPayload = PayloadByType[keyof PayloadByType];
